import { Box } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo } from 'react';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { useSelectedEmployeeId } from '~/hooks/employee';
import { useYupValidationResolver } from '~/hooks/useYupValidationResolver';
import { LegacyFAIcon, LegacyFAIcons } from '~/legacy-components/LegacyFAIcon';
import { LegacyMuiButton } from '~/legacy-components/LegacyForms';

import { putEmpVacBalanceData, yearlyVacType } from './data';
import {
  LegacyAbsBlock,
  LegacyAbsBlock2,
  LegacyAbsBlock3,
} from './LegacyAbsField';
import { useSxStyles } from './useStyles';
import { useVacationBalanceData } from './useVacationBalanceData';
import { VacationDaysHistoryDialog } from './VacationDaysHistoryDialog/VacationDaysHistoryDialog';

export type IFormInput = {
  fVacationBalance: number;
  fVacationBalanceBank1: number;
  fVacationBalanceBank2: number;
  fVacationBalanceBank3: number;
  fVacationBalanceEmpId: number;
  fVacationBalanceExtra: number;
  fVacationBalanceSuppl1: number;
  fVacationBalanceSuppl1Spent: number;
  fVacationBalanceSuppl2: number;
  fVacationBalanceSuppl2Spent: number;
};

export const VacationBalanceData = () => {
  const sxStyles = useSxStyles();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const {
    defaultData,
    shortTermAbsences,
    vacHistoryTableData,
    bnEnableVacBank,
    bnEnableVacBalance,
    vacBankType1,
    vacBankType2,
    vacBankType3,
    openAbsHistoryDialog,
    isAbsHistoryDialogOpen,
    closeAbsHistoryDialog,
    extraVacEnum,
    absShortReasonEnum,
    editMode,
    setEditMode,
    vacBankEnum,
  } = useVacationBalanceData();

  // Form validation
  const validationSchema = useMemo(() => {
    return yup.object().shape<IFormInput>({
      fVacationBalance: yup.number().required(t('laFillAllFields')),
      fVacationBalanceBank1: yup.number(),
      fVacationBalanceBank2: yup.number(),
      fVacationBalanceBank3: yup.number(),
      fVacationBalanceEmpId: yup.number(),
      fVacationBalanceExtra: yup.number(),
      fVacationBalanceSuppl1: yup.number(),
      fVacationBalanceSuppl1Spent: yup.number(),
      fVacationBalanceSuppl2: yup.number(),
      fVacationBalanceSuppl2Spent: yup.number(),
    });
  }, [t]);

  const resolver = useYupValidationResolver(validationSchema);

  const { selectedEmployeeId } = useSelectedEmployeeId();
  const empId = String(selectedEmployeeId);

  // Setup form
  const initialFieldValues: IFormInput | {} = useMemo(() => {
    if (!!defaultData) {
      return {
        ...defaultData,
      };
    }
    return {};
  }, [defaultData]);

  const form = useForm<IFormInput>({
    mode: 'all',
    defaultValues: initialFieldValues,
    resolver: resolver as never,
  });

  const { control, reset, handleSubmit: handleSubmit2 } = form;

  useEffect(() => {
    reset(initialFieldValues);
  }, [initialFieldValues, reset]);

  const handleFormReset = () => {
    reset();
    setEditMode((prevValue) => !prevValue);
  };

  const handleFormSubmit2 = React.useMemo(() => {
    return handleSubmit2(
      (formData) => {
        const postData: IFormInput = {
          fVacationBalance: formData.fVacationBalance,
          fVacationBalanceBank1: formData.fVacationBalanceBank1,
          fVacationBalanceBank2: formData.fVacationBalanceBank2,
          fVacationBalanceBank3: formData.fVacationBalanceBank3,
          fVacationBalanceEmpId: formData.fVacationBalanceEmpId,
          fVacationBalanceExtra: formData.fVacationBalanceExtra,
          fVacationBalanceSuppl1: formData.fVacationBalanceSuppl1,
          fVacationBalanceSuppl1Spent: formData.fVacationBalanceSuppl1Spent,
          fVacationBalanceSuppl2: formData.fVacationBalanceSuppl2,
          fVacationBalanceSuppl2Spent: formData.fVacationBalanceSuppl2Spent,
        };

        (async () => {
          try {
            const response = await putEmpVacBalanceData(
              postData,
              parseInt(empId),
            );
            if (response) {
              enqueueSnackbar(t('change_saved'), {
                variant: 'success',
              });
              setEditMode(false);
            }
          } catch (error) {
            enqueueSnackbar(t('errorMsg'), {
              variant: 'error',
            });
          }
        })();
      },
      (errors) => {
        Object.values(errors).forEach((fieldError) => {
          enqueueSnackbar(fieldError.message, {
            variant: 'error',
          });
        });
      },
    );
  }, [enqueueSnackbar, handleSubmit2, setEditMode, t, empId]);

  const vacBalanceEnumName = (val: string) => {
    const enumVal = extraVacEnum.find((form) => form.code === val);
    return enumVal ? enumVal.name : '';
  };

  const vacBankEnumName = (val: string) => {
    const enumVal = vacBankEnum.find((form) => form.code === val);
    return enumVal ? enumVal.name : '';
  };

  const VacBalanceTableComponent = (
    <Box component="table" sx={sxStyles.table}>
      <tbody>
        <Box component="tr" sx={sxStyles.tr}>
          <Controller
            name="fVacationBalance"
            control={control}
            render={({ field: { onChange, value } }) => {
              return (
                <LegacyAbsBlock
                  editMode={editMode}
                  label1={`${t('laVacBal')}:`}
                  label2={t('laApproved')}
                  label3={t('laInProgress')}
                  label4={t('laRemaining')}
                  name={'fVacationBalance'}
                  value={value}
                  onChange={onChange}
                  val1={defaultData?.fVacationBalance || 0}
                  val2={
                    defaultData?.fVacationBalanceProjDed.fProjectedApproved || 0
                  }
                  val3={
                    defaultData?.fVacationBalanceProjDed.fProjectedUnapproved ||
                    0
                  }
                  val4={
                    (defaultData &&
                      defaultData?.fVacationBalance -
                        defaultData?.fVacationBalanceProjDed
                          .fProjectedApproved -
                        defaultData?.fVacationBalanceProjDed
                          .fProjectedUnapproved) ||
                    0
                  }
                />
              );
            }}
          />
        </Box>
        {bnEnableVacBank && (
          <>
            {!vacBankType1 && (
              <Box component="tr" sx={sxStyles.tr}>
                <Controller
                  name="fVacationBalanceBank1"
                  control={control}
                  render={({ field: { onChange, value } }) => {
                    return (
                      <LegacyAbsBlock
                        editMode={editMode}
                        label1={`${vacBankEnumName('VACATION_BANK_1')}:`}
                        label2={t('laApproved')}
                        label3={t('laInProgress')}
                        label4={t('laRemaining')}
                        name={'fVacationBalanceBank1'}
                        value={value}
                        onChange={onChange}
                        val1={defaultData?.fVacationBalanceBank1 || 0}
                        val2={
                          defaultData?.fVacationBalanceBank1ProjDed
                            .fProjectedApproved || 0
                        }
                        val3={
                          defaultData?.fVacationBalanceBank1ProjDed
                            .fProjectedUnapproved || 0
                        }
                        val4={
                          (defaultData &&
                            defaultData?.fVacationBalanceBank1 &&
                            defaultData?.fVacationBalanceBank1 -
                              defaultData?.fVacationBalanceBank1ProjDed
                                .fProjectedApproved -
                              defaultData?.fVacationBalanceBank1ProjDed
                                .fProjectedUnapproved) ||
                          0
                        }
                      />
                    );
                  }}
                />
              </Box>
            )}
            {!vacBankType2 && (
              <Box component="tr" sx={sxStyles.tr}>
                <Controller
                  name="fVacationBalanceBank2"
                  control={control}
                  render={({ field: { onChange, value } }) => {
                    return (
                      <LegacyAbsBlock
                        editMode={editMode}
                        label1={`${vacBankEnumName('VACATION_BANK_2')}:`}
                        label2={t('laApproved')}
                        label3={t('laInProgress')}
                        label4={t('laRemaining')}
                        name={'fVacationBalanceBank2'}
                        value={value}
                        onChange={onChange}
                        val1={defaultData?.fVacationBalanceBank2 || 0}
                        val2={
                          defaultData?.fVacationBalanceBank2ProjDed
                            .fProjectedApproved || 0
                        }
                        val3={
                          defaultData?.fVacationBalanceBank2ProjDed
                            .fProjectedUnapproved || 0
                        }
                        val4={
                          (defaultData &&
                            defaultData?.fVacationBalanceBank2 &&
                            defaultData?.fVacationBalanceBank2 -
                              defaultData?.fVacationBalanceBank2ProjDed
                                .fProjectedApproved -
                              defaultData?.fVacationBalanceBank2ProjDed
                                .fProjectedUnapproved) ||
                          0
                        }
                      />
                    );
                  }}
                />
              </Box>
            )}
            {!vacBankType3 && (
              <Box component="tr" sx={sxStyles.tr}>
                <Controller
                  name="fVacationBalanceBank3"
                  control={control}
                  render={({ field: { onChange, value } }) => {
                    return (
                      <LegacyAbsBlock
                        editMode={editMode}
                        label1={`${vacBankEnumName('VACATION_BANK_3')}:`}
                        label2={t('laApproved')}
                        label3={t('laInProgress')}
                        label4={t('laRemaining')}
                        name={'fVacationBalanceBank3'}
                        value={value}
                        onChange={onChange}
                        val1={defaultData?.fVacationBalanceBank3 || 0}
                        val2={
                          defaultData?.fVacationBalanceBank3ProjDed
                            .fProjectedApproved || 0
                        }
                        val3={
                          defaultData?.fVacationBalanceBank3ProjDed
                            .fProjectedUnapproved || 0
                        }
                        val4={
                          (defaultData &&
                            defaultData?.fVacationBalanceBank3 &&
                            defaultData?.fVacationBalanceBank3 -
                              defaultData?.fVacationBalanceBank3ProjDed
                                .fProjectedApproved -
                              defaultData?.fVacationBalanceBank3ProjDed
                                .fProjectedUnapproved) ||
                          0
                        }
                      />
                    );
                  }}
                />
              </Box>
            )}
          </>
        )}
        {!bnEnableVacBank && (
          <>
            <Box component="tr" sx={sxStyles.tr}>
              <Controller
                name="fVacationBalanceExtra"
                control={control}
                render={({ field: { onChange, value } }) => {
                  return (
                    <LegacyAbsBlock
                      editMode={editMode}
                      label1={`${vacBalanceEnumName('EXTRA_BALANCE')}:`}
                      label2={t('laApproved')}
                      label3={t('laInProgress')}
                      label4={t('laRemaining')}
                      name={'fVacationBalanceExtra'}
                      value={value}
                      onChange={onChange}
                      val1={defaultData?.fVacationBalanceExtra || 0}
                      val2={
                        defaultData?.fVacationBalanceExtraProjDed
                          .fProjectedApproved || 0
                      }
                      val3={
                        defaultData?.fVacationBalanceExtraProjDed
                          .fProjectedUnapproved || 0
                      }
                      val4={
                        (defaultData &&
                          defaultData?.fVacationBalanceExtra &&
                          defaultData?.fVacationBalanceExtra -
                            defaultData?.fVacationBalanceExtraProjDed
                              .fProjectedApproved -
                            defaultData?.fVacationBalanceExtraProjDed
                              .fProjectedUnapproved) ||
                        0
                      }
                    />
                  );
                }}
              />
            </Box>
            <Box component="tr" sx={sxStyles.tr2}>
              <Controller
                name="fVacationBalanceSuppl1"
                control={control}
                render={({ field: { onChange, value } }) => {
                  return (
                    <LegacyAbsBlock2
                      editMode={editMode}
                      label1={`${vacBalanceEnumName('SUPPLEMENTAL_1_NAME')}:`}
                      label2={`${vacBalanceEnumName('SUPPLEMENTAL_1_TOTAL')}:`}
                      name={'fVacationBalanceSuppl1'}
                      value={value}
                      onChange={onChange}
                    />
                  );
                }}
              />
              <Controller
                name="fVacationBalanceSuppl1Spent"
                control={control}
                render={({ field: { onChange, value } }) => {
                  return (
                    <LegacyAbsBlock3
                      editMode={editMode}
                      label={`${vacBalanceEnumName('SUPPLEMENTAL_1_USED')}:`}
                      name={'fVacationBalanceSuppl1Spent'}
                      value={value}
                      onChange={onChange}
                    />
                  );
                }}
              />
            </Box>
            <Box component="tr" sx={sxStyles.tr2}>
              <Controller
                name="fVacationBalanceSuppl2"
                control={control}
                render={({ field: { onChange, value } }) => {
                  return (
                    <LegacyAbsBlock2
                      editMode={editMode}
                      label1={`${vacBalanceEnumName('SUPPLEMENTAL_2_NAME')}:`}
                      label2={`${vacBalanceEnumName('SUPPLEMENTAL_2_TOTAL')}:`}
                      name={'fVacationBalanceSuppl2'}
                      value={value}
                      onChange={onChange}
                    />
                  );
                }}
              />
              <Controller
                name="fVacationBalanceSuppl2Spent"
                control={control}
                render={({ field: { onChange, value } }) => {
                  return (
                    <LegacyAbsBlock3
                      editMode={editMode}
                      label={`${vacBalanceEnumName('SUPPLEMENTAL_2_USED')}:`}
                      name={'fVacationBalanceSuppl2Spent'}
                      value={value}
                      onChange={onChange}
                    />
                  );
                }}
              />
            </Box>
          </>
        )}
      </tbody>
    </Box>
  );

  const VacationBalanceHistoryDialog = (
    <VacationDaysHistoryDialog
      open={isAbsHistoryDialogOpen}
      onClose={closeAbsHistoryDialog}
      bnEnableVacBank={bnEnableVacBank}
      vacHistoryTableData={vacHistoryTableData}
    />
  );

  const absEnumReason = (val: string) => {
    const enumVal = absShortReasonEnum.find((form) => form.extCode === val);
    return enumVal ? enumVal.name : '';
  };
  const calculateDaysTaken = (year: number) => {
    return shortTermAbsences
      .filter(
        (opt) =>
          !opt.fAbsenceShortSupersededReason &&
          opt.fAbsenceShortSupersededReason !== 'DELETE',
      )
      .reduce((accumulator: number, currentValue) => {
        if (year === currentValue.fAbsenceShortBalanceYear) {
          return accumulator + (currentValue?.fAbsenceShortDays ?? 0);
        }
        return accumulator;
      }, 0);
  };
  const yearlyVacBalTable = (
    <Box component="table" sx={sxStyles.table2}>
      <thead>
        <tr>
          <th>{t('laYear')}</th>
          <th>{t('laAbsenceReason')}</th>
          <th>{t('laRemainingVacationDays')}</th>
          <th>{t('laHolidaysTaken')}</th>
        </tr>
      </thead>
      <tbody>
        {defaultData?.fYearlyVacationBalances.map(
          (data: yearlyVacType, index: number) => {
            return (
              <tr key={index}>
                <Box component="td" sx={sxStyles.td2}>
                  {data.fHolidayYear}
                </Box>
                <Box component="td" sx={sxStyles.td2}>
                  {data.fHolidayCode ? absEnumReason(data.fHolidayCode) : ''}
                </Box>
                <Box component="td" sx={sxStyles.td2}>
                  {data.fHolidayBalance}
                </Box>
                <Box component="td" sx={sxStyles.td2}>
                  {data.fHolidayYear && calculateDaysTaken(data.fHolidayYear)}
                </Box>
              </tr>
            );
          },
        )}
      </tbody>
    </Box>
  );

  return (
    <>
      <form onSubmit={(e) => e.preventDefault()}>
        <Box sx={sxStyles.actionDiv}>
          {editMode ? (
            <>
              <LegacyMuiButton
                onClick={handleFormReset}
                startIcon={<LegacyFAIcon FAIcon={LegacyFAIcons.Close} />}
                sx={sxStyles.button}
              >
                {t('laCancel')}
              </LegacyMuiButton>
              <LegacyMuiButton
                onClick={handleFormSubmit2}
                startIcon={<LegacyFAIcon FAIcon={LegacyFAIcons.Save} />}
                sx={sxStyles.button}
              >
                {t('laSave')}
              </LegacyMuiButton>
            </>
          ) : (
            <LegacyMuiButton
              onClick={() => setEditMode((prevValue) => !prevValue)}
              startIcon={<LegacyFAIcon FAIcon={LegacyFAIcons.Edit} />}
              sx={sxStyles.button}
            >
              {t('laEdit')}
            </LegacyMuiButton>
          )}
          <LegacyMuiButton
            onClick={() => openAbsHistoryDialog()}
            startIcon={<LegacyFAIcon FAIcon={LegacyFAIcons.List} />}
            sx={sxStyles.button}
          >
            {t('laHistory')}
          </LegacyMuiButton>
        </Box>
        <Box sx={sxStyles.tableDiv}>{VacBalanceTableComponent}</Box>
      </form>

      {bnEnableVacBalance &&
        (defaultData && defaultData.fYearlyVacationBalances.length > 0 ? (
          <>{yearlyVacBalTable}</>
        ) : (
          ''
        ))}
      {VacationBalanceHistoryDialog}
    </>
  );
};
